import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, LogOut, Download, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSubscribers = () => {
  const { logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-signups`, {
        headers: getAuthHeader()
      });
      setSubscribers(response.data);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportCSV = () => {
    const csv = [
      'Email,Discount Code,Signup Date',
      ...subscribers.map(s => `${s.email},${s.discountCode},${s.createdAt}`)
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'goat_subscribers.csv';
    a.click();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#09090b] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="font-heading font-bold text-lg tracking-tight">
              G.O.A.T. Admin
            </span>
            <nav className="hidden md:flex gap-4">
              <Link
                to="/admin/dashboard"
                className="text-zinc-400 hover:text-white font-medium text-sm uppercase tracking-wider"
              >
                Products
              </Link>
              <Link
                to="/admin/orders"
                className="text-zinc-400 hover:text-white font-medium text-sm uppercase tracking-wider"
              >
                Orders
              </Link>
              <Link
                to="/admin/subscribers"
                className="text-white font-medium text-sm uppercase tracking-wider"
              >
                Subscribers
              </Link>
              <Link
                to="/admin/account"
                className="text-zinc-400 hover:text-white font-medium text-sm uppercase tracking-wider"
              >
                My Account
              </Link>
            </nav>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-zinc-400 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-2xl uppercase tracking-tight flex items-center gap-3">
              <Mail className="w-6 h-6" />
              Email Subscribers
            </h1>
            <p className="text-zinc-500 mt-1">{subscribers.length} subscribers</p>
          </div>
          {subscribers.length > 0 && (
            <Button
              onClick={exportCSV}
              variant="outline"
              className="font-bold uppercase tracking-wider"
              data-testid="export-csv"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
        </div>

        {/* Subscribers Table */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-zinc-400" />
          </div>
        ) : subscribers.length === 0 ? (
          <div className="text-center py-12 bg-zinc-50 border border-zinc-200">
            <Mail className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-500">No subscribers yet</p>
            <p className="text-zinc-400 text-sm mt-1">
              Email signups will appear here when visitors subscribe
            </p>
          </div>
        ) : (
          <div className="border border-zinc-200 overflow-hidden" data-testid="subscribers-table">
            <table className="w-full">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Email</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider hidden md:table-cell">Discount Code</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Signup Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {subscribers.map((subscriber, index) => (
                  <tr key={subscriber.id || index} className="hover:bg-zinc-50">
                    <td className="px-4 py-4 font-medium">
                      {subscriber.email}
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <span className="bg-[#00ff9d]/10 text-[#00cc7d] font-bold text-xs px-2 py-1 uppercase">
                        {subscriber.discountCode}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-zinc-600">
                      {formatDate(subscriber.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};
